/**
 * Duplicate Properties Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles multiple properties duplication logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { MULTIPLE_PROPERTIES_CONFIG } from './entityConfigs'
import { COPY_PROPERTIES } from '@/housefolios-components/Property/mutations'
import { GET_PROPERTIES } from '@/housefolios-components/Properties/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'

/**
 * Duplicate Properties Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.propertyIds - Array of property IDs to duplicate
 * @param {Array} props.properties - Array of property objects to duplicate
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {string} props.buttonText - Custom button text
 */
function DuplicateProperties(props) {
  const {
    propertyIds,
    properties,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    buttonText = '',
    ...otherProps
  } = props

  // Local state for portfolio selection
  const [selectedPortfolio, setSelectedPortfolio] = useState(
    initialSelectedPortfolio,
  )

  // GraphQL mutation
  const [copyProperties] = useMutation(COPY_PROPERTIES)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return
    }

    try {
      const result = await copyProperties({
        awaitRefetchQueries: true,
        variables: {
          propertyIds: propertyIds,
          portfolioId: selectedPortfolio,
          propertiesInput: additionalState, // Pass additional fields
        },
        refetchQueries: [
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: [selectedPortfolio],
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: portfolios.map((p) => p._id),
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar(
        `${propertyIds.length} properties were duplicated successfully`,
        {
          variant: 'success',
          autoHideDuration: 3000,
        },
      )

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate properties:', error)
      enqueueSnackbar('Properties failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  // const { limits = {}, activeOrg } = session.me
  // const { monthlyCount, bonusProperties = 0 } =
  //   activeOrg.propertyCount
  // const LIMIT = limits.propertyLimit + bonusProperties
  // const overLimit = monthlyCount + propertyIds?.length > LIMIT

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="properties"
      entityId={propertyIds.join(',')} // Use comma-separated IDs for display
      entity={{ name: `${propertyIds.length} Properties` }} // Create a display entity
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      onPortfolioChange={setSelectedPortfolio}
      setSelected={setSelected}
      session={session}
      config={MULTIPLE_PROPERTIES_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={false} // Multiple properties don't typically use sidebar menu
      underwriting={false} // Multiple properties don't have underwriting context
      buttonText={buttonText}
    />
  )
}

// PropTypes - including rendering props
DuplicateProperties.propTypes = {
  propertyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  properties: PropTypes.arrayOf(PropTypes.object),
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicateProperties
